import {
  Alert,
  Box,
  Button,
  Grid2,
  InputAdornment,
  Link,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { ReactComponent as SearchIcon } from "../../assets/search.svg";
import ViewUserMeals from "../Modals/ViewUserMeals";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import MealCard from "../Meals/MealCard";
import {
  selectLoading,
  selectMealsPagination,
  selectRecentMealData
} from "../../features/meals/mealsSelectors";
import { getRecentMeals } from "../../features/meals/mealsSlice";
import { favoriteMeal, likeMeal } from "../../api/modApi";
import useThemeColor from "../../utils/useTheme";
import { selectCurrentUser } from "../../features/auth/authSelectors";

const Meals = ({
  setOpenSubscriptionModal,
  isViewingUserMeals,
  setIsViewingUserMeals
}) => {
  const [isRefresh, setIsRefresh] = React.useState(false);
  const dispatch = useDispatch();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("favorites");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const loader = useSelector(selectLoading);
  const mealsData = useSelector(selectRecentMealData);
  const mealPagination = useSelector(selectMealsPagination);
  const styles = useThemeColor();
  const [localMealsData, setLocalMealsData] = useState([]);
  const userData = useSelector(selectCurrentUser);

  // Infinite scroll states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerPage = 20;
  const observerRef = useRef(null);
  const loadingRef = useRef(null);

  useEffect(() => {
    if (activeTab === "favorites") {
      const filteredData = mealsData?.filter((item) => item.favouriteByUser);
      setLocalMealsData(filteredData);
      setHasMore(false); // Favorites tab doesn't use pagination
    } else {
      // For infinite scroll, append new data instead of replacing
      if (page === 1) {
        setLocalMealsData(mealsData);
      } else {
        // Avoid duplicates when appending new data
        const newMealIds = new Set(mealsData.map((meal) => meal._id));
        const uniqueExistingMeals = localMealsData.filter(
          (meal) => !newMealIds.has(meal._id)
        );
        setLocalMealsData([...uniqueExistingMeals, ...mealsData]);
      }

      // Check if there are more pages to load
      setHasMore(page < mealPagination?.totalPages);
    }
    setIsLoadingMore(false);
  }, [mealsData, activeTab, page, mealPagination?.totalPages]);

  useEffect(() => {
    setPage(1);
    setLocalMealsData([]);
    setHasMore(true);
  }, [activeTab, searchValue]);

  useEffect(() => {
    if (
      !userData?.isFailedPayment &&
      !(
        userData?.isAdmin === 1 ||
        ["Coach", "Company"].includes(userData?.userType)
      ) &&
      !userData?.isSubscriptionActive &&
      (userData?.isTrainingFreeTrialExpired ||
        userData?.isDiaryFreeTrialExpired)
    ) {
      setOpenSubscriptionModal(true);
    }
  }, [userData]);

  useEffect(() => {
    if (activeTab) {
      if (!searchValue || searchValue.length > 3 || isRefresh) {
        dispatch(
          getRecentMeals({
            searchTerm: searchValue,
            currentDate: dayjs().format("YYYY-MM-DD"),
            type: activeTab,
            ...(activeTab !== "favorites" && {
              limit: itemsPerPage,
              page: page
            })
          })
        );
        setIsRefresh(false);
      }
    }
  }, [dispatch, isViewingUserMeals, activeTab, isRefresh, searchValue, page]);

  React.useEffect(() => {
    // Setup Intersection Observer for infinite scrolling
    if (activeTab !== "favorites") {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore && !isLoadingMore && !loader) {
            loadMoreMeals();
          }
        },
        { threshold: 0.5 }
      );

      const currentObserverRef = observerRef.current;
      const currentLoadingRef = loadingRef.current;

      if (currentLoadingRef) {
        currentObserverRef.observe(currentLoadingRef);
      }

      return () => {
        if (currentLoadingRef && currentObserverRef) {
          currentObserverRef.unobserve(currentLoadingRef);
        }
      };
    }
  }, [hasMore, isLoadingMore, loader, activeTab, localMealsData]);

  const loadMoreMeals = () => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1);
    }
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
    const scrollableElement = document.getElementById("dashboard-main");
    if (scrollableElement) {
      scrollableElement.scrollTo(0, 0);
    }
  }, [activeTab]);

  const activeStyles = useCallback(
    (currentTab) => {
      return currentTab === activeTab
        ? {
            background: "#368Ad0",
            color: "#FCFCFC",
            border: "none",
            textTransform: "none"
          }
        : {
            border: "1px solid #6B7280",
            color: "#6B7280",
            textTransform: "none"
          };
    },
    [activeTab]
  );

  const handleLikeToggle = async (mealId) => {
    const updatedMeals = localMealsData.map((meal) =>
      meal._id === mealId
        ? {
            ...meal,
            likedByUser: meal.likedByUser ? 0 : 1,
            likeCount: meal.likedByUser
              ? meal.likeCount - 1
              : meal.likeCount + 1
          }
        : meal
    );

    setLocalMealsData([...updatedMeals]);
    await likeMeal(mealId);
  };

  const handleFavoriteToggle = async (mealId) => {
    const updatedMeals = localMealsData.map((meal) =>
      meal._id === mealId
        ? {
            ...meal,
            favouriteByUser: meal.favouriteByUser ? 0 : 1
          }
        : meal
    );

    setLocalMealsData([...updatedMeals]);
    await favoriteMeal(mealId);
    const isFavorite = updatedMeals.find(
      (meal) => meal._id === mealId
    ).favouriteByUser;
    if (isFavorite) {
      setSnackbarMessage(
        "This MOD has been added to your Meal Library Favorites"
      );
      setOpenSnackbar(true);
    }
  };

  return !(
    userData?.isAdmin === 1 || ["Coach", "Company"].includes(userData?.userType)
  ) &&
    !userData?.isSubscriptionActive &&
    (userData?.isTrainingFreeTrialExpired ||
      userData?.isDiaryFreeTrialExpired) ? (
    <></>
  ) : (
    <Box pb={10}>
      {Object.keys(isViewingUserMeals).length > 0 ? (
        <ViewUserMeals
          isViewingUserMeals={isViewingUserMeals}
          setIsViewingUserMeals={setIsViewingUserMeals}
        />
      ) : (
        <>
          <Snackbar
            open={openSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            autoHideDuration={4000}
            onClose={() => setOpenSnackbar(false)}
          >
            <Alert
              onClose={() => setOpenSnackbar(false)}
              severity={"success"}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <Box
            display="flex"
            padding={{
              lg: "20px 20px 40px 32px",
              sm: "40px 32px",
              xs: "20px 10px 0 15px"
            }}
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
          >
            <Box
              display="flex"
              flexDirection="column"
              gap="24px"
              sx={{
                width:
                  activeTab === "favorites" && !localMealsData?.length
                    ? "100%"
                    : "unset"
              }}
            >
              <Typography
                fontSize={"30px"}
                fontWeight={500}
                color={styles.color}
              >
                Meal Library
              </Typography>
              <TextField
                variant="outlined"
                placeholder="Search..."
                size="small"
                fullWidth
                onChange={(e) => setSearchValue(e.target.value)}
                value={searchValue}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          style={{ height: "17px", color: "#4B5563" }}
                        />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    padding: "14px 11px",
                    height: "100%",
                    borderRadius: 2,
                    color: "#6B7280",
                    "& fieldset": {
                      borderColor: "#358ad0 !important"
                    }
                  },
                  "& .Mui-focused": {
                    "& fieldset": {
                      borderColor: "#358ad0 !important"
                    }
                  },
                  height: "48px",
                  width: !isMobile ? "387px" : "unset",
                  fontSize: "14px",
                  background: styles.background,
                  borderRadius: 2
                }}
              />
              <Box display="flex" gap="10px" textTransform="none">
                <Button
                  onClick={() => {
                    if (loader) return;
                    setActiveTab("favorites");
                  }}
                  sx={activeStyles("favorites")}
                >
                  My Favorites
                </Button>
                <Button
                  onClick={() => {
                    if (loader) return;
                    setActiveTab("popular");
                  }}
                  sx={activeStyles("popular")}
                >
                  Most Popular
                </Button>
                <Button
                  onClick={() => {
                    if (loader) return;
                    setActiveTab("recent");
                  }}
                  sx={activeStyles("recent")}
                  variant="outlined"
                >
                  Most Recent
                </Button>
              </Box>
              <Box>
                {loader && page === 1 ? (
                  <Grid2
                    container
                    spacing={isMobile ? 1 : 2.5}
                    rowSpacing={isMobile ? 1 : 4}
                  >
                    {Array.from(new Array(4)).map((item, index) => (
                      <Grid2 item xs={2} key={index}>
                        <Box key={index} sx={{ width: 210, marginRight: 0.5 }}>
                          {item ? (
                            <img
                              style={{ width: 210, height: 118 }}
                              alt={item.title}
                              src={item.src}
                              loading="lazy"
                            />
                          ) : (
                            <Skeleton
                              sx={{ background: "gray" }}
                              variant="rectangular"
                              height={isMobile ? "160px" : "200px"}
                              width={isMobile ? "160px" : "200px"}
                            />
                          )}

                          <Box sx={{ pt: 0.5 }}>
                            <Skeleton sx={{ background: "gray" }} />
                            <Skeleton sx={{ background: "gray" }} width="60%" />
                          </Box>
                        </Box>
                      </Grid2>
                    ))}
                  </Grid2>
                ) : activeTab === "favorites" && !localMealsData?.length ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={"50vh"}
                  >
                    <Typography color={styles.modLabel}>
                      You have not saved any meals. Head on over to the{" "}
                      <Link
                        sx={{
                          textDecoration: "none",
                          cursor: "pointer",
                          color: "#368AD0"
                        }}
                        onClick={() => setActiveTab("popular")}
                      >
                        Most Popular
                      </Link>{" "}
                      or{" "}
                      <Link
                        sx={{
                          textDecoration: "none",
                          cursor: "pointer",
                          color: "#368AD0"
                        }}
                        onClick={() => setActiveTab("recent")}
                      >
                        Most Recent
                      </Link>{" "}
                      tabs to see what the community is sharing.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Grid2
                      container
                      spacing={isMobile ? 1 : 2.5}
                      rowSpacing={isMobile ? 1 : 4}
                    >
                      {localMealsData?.map((meal, index) => (
                        <Grid2 item xs={6} sm={6} key={`${meal._id}-${index}`}>
                          <MealCard
                            key={`${meal._id}-${index}`}
                            searchValue={searchValue}
                            meal={meal}
                            setIsViewingUserMeals={setIsViewingUserMeals}
                            currentTab={activeTab}
                            isFromViewingMeal={false}
                            onLikeToggle={(id) => handleLikeToggle(id)}
                            onFavourite={(id) => handleFavoriteToggle(id)}
                            setIsRefresh={setIsRefresh}
                            setOpenSnackbar={setOpenSnackbar}
                            setSnackbarMessage={setSnackbarMessage}
                          />
                        </Grid2>
                      ))}
                    </Grid2>

                    {/* Loading indicator at the bottom for infinite scroll */}
                    {activeTab !== "favorites" && (
                      <Box
                        ref={loadingRef}
                        display="flex"
                        justifyContent="center"
                        my={4}
                        height="40px"
                      >
                        {isLoadingMore && hasMore && (
                          <Box display="flex" gap={2} alignItems="center">
                            <Typography color={styles.color}>
                              Loading more meals...
                            </Typography>
                            {Array.from(new Array(3)).map((_, index) => (
                              <Skeleton
                                key={index}
                                sx={{ background: "gray" }}
                                variant="circular"
                                width={10}
                                height={10}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Meals;
